import React, { useState, useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import config from '../config';
import { useData } from "../data/DataContext";
import { CATEGORIES, formatDate } from "../data/blogHelpers";
import "./BlogsPage.css";

export default function BlogsPage() {
  const { blogs, loading } = useData();
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");

  const navigate = useNavigate();
  const location = useLocation();

  // ✅ FIXED contact scroll handler
  const handleContactClick = (e) => {
    e.preventDefault();

    const scrollToContact = () => {
      const el = document.getElementById("contact");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };

    if (location.pathname !== "/") {
      navigate("/");

      // wait for route change
      setTimeout(scrollToContact, 300);
      return;
    }

    scrollToContact();
  };

  const filtered = useMemo(() => {
    return blogs.filter((blog) => {
      const matchCat =
        activeCategory === "All" || blog.category === activeCategory;
      const q = search.toLowerCase();
      const matchSearch =
        !q ||
        blog.title.toLowerCase().includes(q) ||
        blog.excerpt.toLowerCase().includes(q) ||
        blog.tags.some((t) => t.toLowerCase().includes(q));
      return matchCat && matchSearch;
    });
  }, [blogs, search, activeCategory]);

  const featured = filtered.filter((b) => b.featured).slice(0, 1)[0];
  const rest = filtered.filter((b) => !featured || b.id !== featured.id);

  if (loading) {
    return (
      <div className="blogs-page">
        <section className="blogs-hero">
          <div className="blogs-hero__inner">
            <h1 className="blogs-hero__title">CFO Insights & Finance Strategy</h1>
          </div>
        </section>
        <div className="blogs-body">
          <p className="blogs-count">Loading articles…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="blogs-page">
      {/* ── Hero Banner ── */}
      <section className="blogs-hero">
        <div className="blogs-hero__inner">
          
          <h1 className="blogs-hero__title">CFO Insights & Finance Strategy</h1>
          <p className="blogs-hero__subtitle">
            Expert perspectives on UAE corporate finance, tax, fundraising, <br/>and
            business growth — from CFOs who've been in the room.
          </p>

          {/* Search */}
          <div className="blogs-search">
            <span className="blogs-search__icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
            </span>
            <input
              type="text"
              placeholder="Search articles…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="blogs-search__input"
            />
            {search && (
              <button className="blogs-search__clear" onClick={() => setSearch("")}>✕</button>
            )}
          </div>
        </div>
      </section>

      <div className="blogs-body">
        {/* ── Category Filter ── */}
        <div className="blogs-filters">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              className={`blogs-filter-btn ${activeCategory === cat ? "active" : ""}`}
              onClick={() => setActiveCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="blogs-empty">
            <p>No articles found for "<strong>{search}</strong>".</p>
            <button onClick={() => { setSearch(""); setActiveCategory("All"); }}>
              Clear filters
            </button>
          </div>
        ) : (
          <>
            {/* ── Featured Article ── */}
            {featured && activeCategory === "All" && !search && (
              <Link to={`/blogs/${featured.id}`} className="blog-featured">
                <div className="blog-featured__img-wrap">
                  <img src={featured.image} alt={featured.title} className="blog-featured__img" />
                  <span className="blog-featured__badge">Featured</span>
                </div>
                <div className="blog-featured__content">
                  <div className="blog-featured__meta">
                    <span className="blog-cat">{featured.category}</span>
                    <span className="blog-sep">·</span>
                    <span>{formatDate(featured.date)}</span>
                    <span className="blog-sep">·</span>
                    <span>{featured.readTime}</span>
                  </div>
                  <h2 className="blog-featured__title">{featured.title}</h2>
                  <p className="blog-featured__excerpt">{featured.excerpt}</p>
                  <div className="blog-featured__tags">
                    {featured.tags.slice(0, 3).map((t) => (
                      <span key={t} className="blog-tag">{t}</span>
                    ))}
                  </div>
                  <span className="blog-featured__cta">
                    Read Article
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                    </svg>
                  </span>
                </div>
              </Link>
            )}

            {/* ── Results count ── */}
            <p className="blogs-count">
              {(activeCategory === "All" && !search ? rest : filtered).length} article{(activeCategory === "All" && !search ? rest : filtered).length !== 1 ? "s" : ""}
              {activeCategory !== "All" && ` in ${activeCategory}`}
              {search && ` matching "${search}"`}
            </p>
            
            
            {/* ── Grid ── */}
            <div className="blogs-grid">
              {(activeCategory === "All" && !search ? rest : filtered).map((blog) => (
                <Link key={blog.id} to={`/blogs/${blog.id}`} className="blog-card">
                  <div className="blog-card__img-wrap">
                    <img src={blog.image} alt={blog.title} className="blog-card__img" />
                    <span className="blog-card__cat">{blog.category}</span>
                  </div>
                  <div className="blog-card__body">
                    <div className="blog-card__meta">
                      <span>{formatDate(blog.date)}</span>
                      <span className="blog-sep">·</span>
                      <span>{blog.readTime}</span>
                    </div>
                    <h3 className="blog-card__title">{blog.title}</h3>
                    <p className="blog-card__excerpt">{blog.excerpt}</p>
                    <div className="blog-card__footer">
                      <div className="blog-card__tags">
                        {blog.tags.slice(0, 2).map((t) => (
                          <span key={t} className="blog-tag">{t}</span>
                        ))}
                      </div>
                      <span className="blog-card__arrow">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                        </svg>
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
{/* ══ CTA BAND ══ */}
        <section className="wcu-cta">
          <div
            className="wcu-cta-bg"
            style={{ backgroundImage: `url(${config.imageBaseURL}${config.paths.banners}/uae-cfobridge2.webp)` }}
          />
          <div className="wcu-cta-overlay" />
          <div className="wcu-cta-inner">
            <h2>Ready to elevate your finance function?</h2>
            <p>
              Let CFO Bridge UAE become the strategic financial partner your business deserves — on-demand expertise, real ROI, zero full-time overhead.
            </p>
            <a href="#contact" className="wcu-btn-cta" onClick={handleContactClick}>
                 Schedule Consultation
                </a>
          </div>
        </section>
      
    </div>
  );
}